import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Button, Col, Container, Row} from 'react-bootstrap';
import {useTranslation} from 'react-i18next';
import {ReactComponent as DownloadIcon} from '../utilities/icons/download.svg';
import {ReactComponent as RemoveIcon} from '../utilities/icons/remove.svg';
import {ReactComponent as UndoIcon} from '../utilities/icons/rotate-left.svg';
import {downloadVttResourceUrl, listVttResources} from '../../hooks/record/vttResourcesApi';
import {chapterLanguage} from '../../utils/chapterLanguage';
import './RecordSubtitleDownloadLinks.css';

const LANGUAGE_PRIORITIES = {fi: 0, sv: 1, en: 2};
const LANGUAGE_NAMES = {fi: 'finnish', sv: 'swedish', en: 'english'};

const downloadChapter = async (event, filename) => {
    event.preventDefault();

    try {
        const response = await fetch(event.currentTarget.href);
        if (!response.ok) return;

        const downloadUrl = URL.createObjectURL(await response.blob());
        const downloadLink = document.createElement('a');
        downloadLink.href = downloadUrl;
        downloadLink.download = filename;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        downloadLink.remove();
        window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000);
    } catch {
        return;
    }
};

const RecordChapterDownloadLinks = ({identifier, refresh = 0, chapters = {}, onChange, disabled = false}) => {
    const {t} = useTranslation();
    const [storedChapters, setStoredChapters] = useState([]);
    const [chaptersLoaded, setChaptersLoaded] = useState(!identifier);

    useEffect(() => {
        if (!identifier) {
            setStoredChapters([]);
            setChaptersLoaded(true);
            return undefined;
        }

        let active = true;
        setChaptersLoaded(false);
        listVttResources(identifier, 'chapter')
            .then(async response => {
                if (!response.ok) throw new Error('Could not load chapters');
                return response.json();
            })
            .then(items => {
                if (active) setStoredChapters(Array.isArray(items) ? items : []);
            })
            .catch(() => {
                if (active) setStoredChapters([]);
            })
            .finally(() => {
                if (active) setChaptersLoaded(true);
            });

        return () => {
            active = false;
        };
    }, [identifier, refresh]);

    const chapterFiles = storedChapters
        .filter(chapter => chapter?.language && LANGUAGE_PRIORITIES[chapterLanguage(chapter.language)] !== undefined)
        .sort((a, b) => LANGUAGE_PRIORITIES[chapterLanguage(a.language)] - LANGUAGE_PRIORITIES[chapterLanguage(b.language)]);

    if (!chaptersLoaded || chapterFiles.length === 0) {
        return null;
    }

    return (
        <div className="record-form-section">
            <h2 className="record-form-section-header">{t('record_form_section_chapter_downloads')}</h2>
            <Row className="mb-4">
                <Col>
                    <Container>
                        <Row>
                            <Col>
                                <ul className="blockquote record-subtitle-download-link-list">
                                    {chapterFiles.map(chapter => {
                                        const language = chapterLanguage(chapter.language);
                                        const markedForDeletion = chapters?.[language] === null;
                                        const filename = chapter.title || `chapter_${language}.vtt`;
                                        return (
                                            <li key={language}>
                                                <div className="d-flex align-items-center">
                                                <span className="card-tag badge bg-success">
                                                    {t(`record_subtitle_video_text_track_file_${LANGUAGE_NAMES[language]}`)}
                                                </span>
                                                    <div className="record-subtitle-download-link-item">
                                                        <div className="record-subtitle-download-link-item-link">
                                                            <DownloadIcon className="record-subtitle-download-icon"/>
                                                            <a
                                                                title={filename}
                                                                className={markedForDeletion ? 'record-subtitle-download-link-deleted' : 'record-subtitle-download-link'}
                                                                download={filename}
                                                                href={downloadVttResourceUrl(identifier, 'chapter', chapter.language)}
                                                                onClick={(event) => downloadChapter(event, filename)}
                                                            >
                                                                {filename}
                                                            </a>
                                                        </div>
                                                        {onChange && (
                                                            <div className="record-subtitle-download-link-remove-button">
                                                                <Button
                                                                    className="remove-subtitle-button"
                                                                    type="button"
                                                                    variant="link"
                                                                    disabled={disabled}
                                                                    aria-label={markedForDeletion ? t('record_chapters_restore_file') : t('record_chapters_delete_file')}
                                                                    onClick={() => {
                                                                        if (markedForDeletion) {
                                                                            const {
                                                                                [language]: _removed,
                                                                                ...remainingChapters
                                                                            } = chapters;
                                                                            onChange(Object.keys(remainingChapters).length > 0 ? remainingChapters : undefined);
                                                                        } else {
                                                                            onChange({...chapters, [language]: null});
                                                                        }
                                                                    }}
                                                                >
                                                                    {markedForDeletion ? <UndoIcon width="1.1em" height="1.1em"/> :
                                                                        <RemoveIcon width="1.1em" height="1.1em"/>}
                                                                    {markedForDeletion ? t('record_chapters_restore_file') : t('record_chapters_delete_file')}
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </Col>
                        </Row>
                    </Container>
                </Col>
            </Row>
        </div>
    );
};

RecordChapterDownloadLinks.propTypes = {
    identifier: PropTypes.string,
    refresh: PropTypes.number,
    chapters: PropTypes.object,
    onChange: PropTypes.func,
    disabled: PropTypes.bool
};

export default RecordChapterDownloadLinks;
